import type { SuggestionStatus } from '@adweb/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Lightbulb, Pencil } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { apiClient } from '../../lib/api-client';
import { StatusPill } from '../requests/RequestThread';

import {
  type AdminSuggestion,
  SUGGESTION_STATUS_META,
  apiErrorMessage,
  formatDate,
} from './suggestion-meta';
import { GainPill, SuggestionIconChip } from './SuggestionCard';

export function SuggestionStatusPill({ status }: { status: SuggestionStatus }) {
  const meta = SUGGESTION_STATUS_META[status];
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium',
        meta.cls,
      )}
    >
      {meta.label}
    </span>
  );
}

export function targetLabel(s: Pick<AdminSuggestion, 'targetAll' | 'targets'>): string {
  if (s.targetAll) return 'Toutes les organisations clientes';
  return s.targets.length === 1 ? s.targets[0]!.name : `${s.targets.length} organisations`;
}

/**
 * Fiche d'une suggestion côté Adweb : aperçu, diffusion (publier, archiver…) et
 * réponses des clients avec un lien vers leur demande.
 */
export function SuggestionAdminModal({
  suggestionId,
  onClose,
  onEdit,
}: {
  suggestionId: string | null;
  onClose: () => void;
  onEdit: (s: AdminSuggestion) => void;
}) {
  const qc = useQueryClient();
  const [notify, setNotify] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNotify(true);
    setConfirmDelete(false);
    setError(null);
  }, [suggestionId]);

  const detail = useQuery({
    queryKey: ['suggestions', 'detail', suggestionId],
    queryFn: async () =>
      (await apiClient.get<AdminSuggestion>(`/suggestions/${suggestionId}`)).data,
    enabled: !!suggestionId,
  });
  const s = detail.data;

  const status = useMutation({
    mutationFn: (next: SuggestionStatus) =>
      apiClient.post(`/suggestions/${suggestionId}/status`, {
        status: next,
        notify: next === 'PUBLISHED' && notify,
      }),
    onMutate: () => setError(null),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suggestions'] }),
    onError: (err) => setError(apiErrorMessage(err, 'Le changement n’a pas abouti.')),
  });

  const remove = useMutation({
    mutationFn: () => apiClient.delete(`/suggestions/${suggestionId}`),
    onMutate: () => setError(null),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['suggestions'] });
      onClose();
    },
    onError: (err) => setError(apiErrorMessage(err, 'La suppression a échoué.')),
  });

  const busy = status.isPending || remove.isPending;
  const interested = s?.responses?.filter((r) => r.type === 'INTERESTED') ?? [];
  const dismissed = s?.responses?.filter((r) => r.type === 'DISMISSED') ?? [];

  return (
    <Modal
      open={!!suggestionId}
      onClose={onClose}
      title={s?.title ?? 'Suggestion'}
      size="lg"
      footer={
        <Button variant="secondary" type="button" onClick={onClose}>
          Fermer
        </Button>
      }
    >
      {detail.isLoading && <p className="text-xs text-slate-400">Chargement…</p>}
      {detail.isError && (
        <p role="alert" className="text-xs text-red-600">
          La suggestion n’a pas pu être chargée.
        </p>
      )}
      {s && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <SuggestionStatusPill status={s.status} />
            <span>{targetLabel(s)}</span>
            {s.publishedAt && (
              <>
                <span>·</span>
                <span>Publiée le {formatDate(s.publishedAt)}</span>
              </>
            )}
          </div>

          {/* Aperçu de ce que voient les clients. */}
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="flex items-start gap-4">
              <SuggestionIconChip suggestion={s} size="lg" />
              <div className="min-w-0 space-y-2">
                <p className="text-sm font-medium text-slate-900">{s.tagline}</p>
                <div className="flex flex-wrap items-center gap-2">
                  {s.category && (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                      {s.category}
                    </span>
                  )}
                  <GainPill hours={s.hoursSavedPerMonth} />
                </div>
              </div>
            </div>
            <p className="mt-3 whitespace-pre-line text-sm text-slate-700">{s.description}</p>
            {s.example && (
              <div className="mt-3 rounded-lg bg-slate-50 p-3">
                <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-slate-500">
                  <Lightbulb className="h-3.5 w-3.5" aria-hidden="true" />
                  Exemple concret
                </div>
                <p className="whitespace-pre-line text-sm text-slate-800">{s.example}</p>
              </div>
            )}
            {!s.targetAll && (
              <p className="mt-3 text-xs text-slate-500">
                Proposée à : {s.targets.map((t) => t.name).join(', ')}
              </p>
            )}
          </div>

          {/* Diffusion */}
          <div className="space-y-3 border-t border-slate-100 pt-4">
            <div className="text-xs font-medium text-slate-500">Diffusion</div>
            {s.status !== 'PUBLISHED' && (
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                />
                Prévenir les clients concernés (e-mail et cloche)
              </label>
            )}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="secondary" onClick={() => onEdit(s)} disabled={busy}>
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                Modifier
              </Button>
              {s.status !== 'PUBLISHED' && (
                <Button size="sm" onClick={() => status.mutate('PUBLISHED')} disabled={busy}>
                  {s.status === 'ARCHIVED' ? 'Republier' : 'Publier'}
                </Button>
              )}
              {s.status === 'PUBLISHED' && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => status.mutate('DRAFT')}
                  disabled={busy}
                >
                  Repasser en brouillon
                </Button>
              )}
              {s.status !== 'ARCHIVED' && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => status.mutate('ARCHIVED')}
                  disabled={busy}
                >
                  Archiver
                </Button>
              )}
              {s.interestedCount + s.dismissedCount === 0 &&
                (confirmDelete ? (
                  <span className="inline-flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-600">Supprimer définitivement ?</span>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => remove.mutate()}
                      disabled={busy}
                    >
                      Oui, supprimer
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>
                      Non
                    </Button>
                  </span>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
                    Supprimer
                  </Button>
                ))}
            </div>
            <p className="text-xs text-slate-500">
              Brouillon : visible des seuls comptes Adweb-Systèmes, le temps de tester le service.
              Archivée : retirée de la vitrine, les demandes déjà reçues restent.
            </p>
            {error && (
              <p role="alert" className="text-xs text-red-600">
                {error}
              </p>
            )}
          </div>

          {/* Réponses des clients */}
          <div className="space-y-3 border-t border-slate-100 pt-4">
            <div className="text-xs font-medium text-slate-500">
              Intérêt des clients · {interested.length} intéressé
              {interested.length > 1 ? 's' : ''} · {dismissed.length} « pas pour moi »
            </div>
            {interested.length === 0 ? (
              <p className="text-sm text-slate-500">Aucune demande pour le moment.</p>
            ) : (
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {interested.map((r) => (
                  <li
                    key={r.organization.id}
                    className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                  >
                    <div className="min-w-0 text-sm">
                      <span className="font-medium text-slate-900">{r.organization.name}</span>
                      <span className="text-slate-500">
                        {' '}
                        · {r.user?.fullName ?? 'Compte supprimé'} · {formatDate(r.createdAt)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {r.request && <StatusPill status={r.request.status} />}
                      {r.requestId && (
                        <Link
                          to={`/demandes?id=${r.requestId}`}
                          className="text-xs font-medium text-brand hover:underline"
                        >
                          Ouvrir la demande
                        </Link>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {dismissed.length > 0 && (
              <p className="text-xs text-slate-500">
                Pas pour eux : {dismissed.map((r) => r.organization.name).join(', ')}
              </p>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
